// The accounts area's calls (room 1: the person, settings and the account's standing), through the doorway
// (doorway.ts). Each sends exactly what its screen sent before it moved here: the same address, method, headers
// and body (tests/accounts-api.test.mjs). The headers differ from call to call only because they always have.
//
// A call with a v2 side goes to v2 when the accounts area's switch puts the visitor there (switch.ts: staff and the
// flag's customers), answered by accounts-v2.ts in v1's shape; every other call here stays on v1 until r1-rewire
// gives it one, and so does one that needs what v2 can't yet do as v1 does (doorway.ts, `V2_LACKS`: the age-gate
// banner's check). The social team's top-up is v2's alone.
//
// `token` is the person's Firebase ID token. Calls of other rooms on the same screens (the settings page's codes,
// orders, receipts and daily gems) stay where they are until their own area moves.
import {
  call,
  lazySide,
  signedIn,
  signedInJson,
  unlessV2Lacks,
} from './doorway.ts'
import type { V2Side } from './doorway.ts'

const AREA = 'accounts'

type Sides = typeof import('./accounts-v2.ts')

const sides = () => import('./accounts-v2.ts')

/**
 * A call's v2 side (accounts-v2.ts), loaded only when the switch puts the visitor on v2. When that module fails to
 * load, the call goes to v1 at `path`, as it would have with the switch off.
 */
const onV2 = (
  name: {
    [K in keyof Sides]: Sides[K] extends V2Side ? K : never
  }[keyof Sides],
  path: string,
): V2Side => lazySide(sides, (loaded) => loaded[name], path)

/** A v2 side that takes more than the token and `init`, as `use` builds it from accounts-v2.ts; as `onV2` otherwise. */
const onV2With = (path: string, use: (loaded: Sides) => V2Side): V2Side =>
  lazySide(sides, use, path)

/**
 * The account document every signed-in page reads (AuthContext): the person's fields and `frontend_data`.
 * 404: this sign-in has no account yet. On v2, made from v2's answers (accounts-v2.ts, `userInfo`).
 */
export const getUserInfo = (token: string, signal?: AbortSignal) =>
  call(
    AREA,
    '/api/get_user_info',
    {
      method: 'GET',
      headers: signedInJson(token),
      ...(signal ? { signal } : {}),
    },
    { token, side: onV2('userInfo', '/api/get_user_info') },
  )

/**
 * The plan in force, with the device id for v1's device rules (AuthContext): `{ status, plan, source, subPlan }`. On
 * v2, from v2's "me" alone, with no call to v1.
 */
export const checkSubscriptionStatus = (token: string, deviceId: string) =>
  call(
    AREA,
    '/api/check_subscription_status',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ device_id: deviceId }),
    },
    { token, side: onV2('subscription', '/api/check_subscription_status') },
  )

/**
 * Settings' own read of the name and bio: the same document, by POST as settings has always asked. On v2, "Read my
 * account" (`GET /v2/me`), answered as v1's `{ username, bio }`, the two fields settings reads.
 */
export const getSettings = (token: string) =>
  call(
    AREA,
    '/api/get_user_info',
    { method: 'POST', headers: signedInJson(token) },
    { token, side: onV2('settings', '/api/get_user_info') },
  )

/** Turns safe mode the other way. v1 takes the uid in the body (and checks it against the token). */
export const toggleSafeMode = (token: string, uid: string) =>
  call(
    AREA,
    '/api/safe_mode',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ uid }),
    },
    { token, side: onV2('flipSafeMode', '/api/safe_mode') },
  )

/** A new username; a refusal answers `{ error }` with v1's words. */
export const updateUsername = (token: string, username: string) =>
  call(
    AREA,
    '/api/update_username',
    {
      method: 'PUT',
      headers: signedInJson(token),
      body: JSON.stringify({ username }),
    },
    {
      token,
      side: onV2With(
        '/api/update_username',
        (loaded) => (t, init) => loaded.changeMe(t, init, { username }),
      ),
    },
  )

/** A new bio; a refusal answers `{ error }` with v1's words. */
export const updateBio = (token: string, bio: string) =>
  call(
    AREA,
    '/api/update_bio',
    {
      method: 'PUT',
      headers: signedInJson(token),
      body: JSON.stringify({ bio }),
    },
    {
      token,
      side: onV2With(
        '/api/update_bio',
        (loaded) => (t, init) => loaded.changeMe(t, init, { bio }),
      ),
    },
  )

/** Asks to delete the account (settings, and a banned person's erasure on the ban screen). */
export const deleteUser = (token: string) =>
  call(
    AREA,
    '/api/delete_user',
    { method: 'POST', headers: signedInJson(token) },
    { token, side: onV2('requestDeletion', '/api/delete_user') },
  )

/** Takes back a deletion within its grace period. 404 or 410: too late. */
export const restoreUser = (token: string) =>
  call(
    AREA,
    '/api/restore_user',
    { method: 'POST', headers: signedInJson(token) },
    { token, side: onV2('cancelDeletion', '/api/restore_user') },
  )

/** A new API key for the person, shown once: `{ api_key }`. */
export const createApiToken = (token: string) =>
  call(AREA, '/api/create-api-token', {
    method: 'GET',
    headers: signedIn(token),
  })

/**
 * The picture page's "set as profile picture": v1 takes the picture's address (`imageId`, as the page has always
 * sent it); v2 takes the picture's id. The page reads nothing from the answer.
 */
export const setProfilePicture = (
  token: string,
  imageUrl: string,
  imageId: string,
) =>
  call(
    AREA,
    '/api/update_profile',
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ imageId: imageUrl }),
    },
    {
      token,
      side: onV2With(
        '/api/update_profile',
        (loaded) => (t, init) =>
          loaded.changeMe(t, init, { profile_picture: imageId }),
      ),
    },
  )

/** The chat screen's own picture: `{ profile_image_url }`. */
export const getProfile = (token: string) =>
  call(
    AREA,
    '/api/get_profile',
    { method: 'GET', headers: signedInJson(token) },
    { token, side: onV2('profileImage', '/api/get_profile') },
  )

/**
 * Starts an age check (the age-gate banner): `{ verificationUrl }`, the provider's page to open. On v1 while v2 knows
 * no country (`V2_LACKS.country`), when v2 would answer that nobody needs one, and while production's v2 has no key
 * for the provider (`V2_LACKS.ageCheck`).
 */
export const startAgeCheck = (token: string) =>
  call(
    AREA,
    '/api/age_gate/start',
    { method: 'POST', headers: signedInJson(token) },
    unlessV2Lacks(['country', 'ageCheck'], {
      token,
      side: onV2('ageCheck', '/api/age_gate/start'),
    }),
  )

/**
 * Staff ban someone (moderation, the picture page): `{ message }`. `keepalive` as the picture page has always sent
 * it, so the ban survives the page closing. v2 needs a reason of 3 to 500 characters, and says so in `error`.
 */
export const banUser = (
  token: string,
  userId: string,
  reason: string,
  { keepalive = false }: { keepalive?: boolean } = {},
) =>
  call(
    AREA,
    '/api/mod/ban_user',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ user_id: userId, reason }),
      ...(keepalive ? { keepalive } : {}),
    },
    {
      token,
      side: onV2With(
        '/api/mod/ban_user',
        (loaded) => (t, init) => loaded.ban(t, init, userId, reason),
      ),
    },
  )

/** Staff lift a ban (moderation, the picture page): `{ message, banned, content_already_deleted }` (v2's `content_already_removed`, answered in v1's word). */
export const unbanUser = (
  token: string,
  userId: string,
  { keepalive = false }: { keepalive?: boolean } = {},
) =>
  call(
    AREA,
    '/api/mod/unban_user',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ user_id: userId }),
      ...(keepalive ? { keepalive } : {}),
    },
    {
      token,
      side: onV2With(
        '/api/mod/unban_user',
        (loaded) => (t, init) => loaded.liftBan(t, init, userId),
      ),
    },
  )

/**
 * The social team's daily test gems (settings): the balance to 5,000, once a UTC day. `{ message }`. Always v2's staff
 * top-up (`POST /v2/staff/gem-grants`), whatever the switch says: v1's `/api/social/claim_daily_gems` was dropped (Andy,
 * 23 Oct 2026; plan task v1-drops), so the live site never asks it. Only a build without v2's address (local, CI) still
 * sends it there.
 */
export const claimSocialGems = (token: string) =>
  call(
    AREA,
    '/api/social/claim_daily_gems',
    { method: 'POST', headers: signedInJson(token) },
    {
      token,
      side: onV2('socialTopUp', '/api/social/claim_daily_gems'),
      ownedByV2: true,
    },
  )
